'use client';

import { useMemo } from 'react';
import { useLocale } from './context.js';
import {
  EMPTY_VALUE,
  answerLabel,
  approvalLabel,
  formatDate,
  formatDateTime,
  formatMoney,
  frequencyLabel,
  leadTypeLabel,
  offerLabel,
  reviewLabel,
  roleLabel,
} from './format.js';

/**
 * The formatters, bound to the active locale.
 *
 * Both consoles used to import a module of `de-DE`-hardcoded helpers. Binding
 * them to the context instead means a language switch reformats dates, money
 * and every enum label without a reload.
 */
export interface BoundFormat {
  dateTime: (iso: string | null | undefined) => string;
  date: (iso: string | null | undefined) => string;
  money: (cents: number | null | undefined) => string;
  leadType: (value: string) => string;
  frequency: (value: string) => string;
  approval: (value: string) => string;
  review: (value: string) => string;
  offer: (value: string) => string;
  role: (value: string) => string;
  answer: (value: string) => string;
  /** The em dash both languages use for an absent value. */
  empty: string;
}

export function useFormat(): BoundFormat {
  const { locale } = useLocale();

  return useMemo<BoundFormat>(
    () => ({
      dateTime: (iso) => formatDateTime(locale, iso),
      date: (iso) => formatDate(locale, iso),
      money: (cents) => formatMoney(locale, cents),
      leadType: (value) => leadTypeLabel(locale, value),
      frequency: (value) => frequencyLabel(locale, value),
      approval: (value) => approvalLabel(locale, value),
      review: (value) => reviewLabel(locale, value),
      offer: (value) => offerLabel(locale, value),
      role: (value) => roleLabel(locale, value),
      answer: (value) => answerLabel(locale, value),
      empty: EMPTY_VALUE,
    }),
    [locale],
  );
}
